import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, TrendingUp, BarChart3, PieChart, History, Wallet, HelpCircle, Menu, ChevronLeft } from 'lucide-react';
interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}
interface SidebarProps {
  onNavigate?: (itemId: string) => void;
}
export function Sidebar({
  onNavigate
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(true);
  const navItems: NavItem[] = [{
    id: 'home',
    label: 'Home',
    icon: <Home size={20} data-id="element-957" />,
    path: '/'
  }, {
    id: 'trade',
    label: 'Trade',
    icon: <TrendingUp size={20} data-id="element-958" />,
    path: '/trade'
  }, {
    id: 'markets',
    label: 'Markets',
    icon: <BarChart3 size={20} data-id="element-959" />,
    path: '/markets'
  }, {
    id: 'analytics',
    label: 'Analytics',
    icon: <PieChart size={20} data-id="element-960" />,
    path: '/analytics'
  }, {
    id: 'portfolio',
    label: 'Portfolio',
    icon: <Wallet size={20} data-id="element-961" />,
    path: '/portfolio'
  }, {
    id: 'history',
    label: 'History',
    icon: <History size={20} data-id="element-962" />,
    path: '/history'
  }];
  const handleItemClick = (item: NavItem) => {
    navigate(item.path);
    onNavigate?.(item.id);
  };
  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };
  return <div className={`
        flex flex-col h-screen bg-background-secondary border-r border-border-subtle
        transition-all duration-200
        ${isExpanded ? 'w-56' : 'w-14'}
      `} data-id="element-963">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-border-subtle" data-id="element-964">
        {isExpanded ? <>
            <div className="flex items-center gap-2" data-id="element-965">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" className="bg-brand-purple rounded p-1" data-id="element-966">
                <path d="M8 10L16 6L24 10V16L16 20L8 16V10Z" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-967" />
                <path d="M16 6V12M16 12L8 16M16 12L24 16" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-968" />
                <path d="M12 14L16 16L20 14" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-969" />
                <path d="M8 22L16 26L24 22" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-970" />
              </svg>
              <span className="font-semibold text-sm text-text-primary" data-id="element-971">
                mExchange
              </span>
            </div>
            <button onClick={() => setIsExpanded(false)} className="p-1 hover:bg-background-elevated rounded transition-colors" data-id="element-972">
              <ChevronLeft size={16} data-id="element-973" />
            </button>
          </> : <button onClick={() => setIsExpanded(true)} className="p-1 hover:bg-background-elevated rounded transition-colors mx-auto" data-id="element-974">
            <Menu size={20} data-id="element-975" />
          </button>}
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2 px-2 relative" data-id="element-976">
        {navItems.map(item => {
        const active = isActive(item.path);
        return <button key={item.id} onClick={() => handleItemClick(item)} className={`
                w-full flex items-center gap-3 px-3 py-3 transition-all rounded-lg mb-1 relative
                ${active ? 'text-white' : 'text-text-secondary hover:bg-white/[0.06] hover:text-text-primary'}
                ${!isExpanded && 'justify-center'}
              `} title={!isExpanded ? item.label : undefined} data-id="element-977">
              {/* Animated background for active item */}
              {active && <motion.div layoutId="activeNav" className="absolute inset-0 bg-brand-purple/15 rounded-lg" initial={false} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 35,
            mass: 0.8
          }} data-id="element-978" />}

              {/* Icon - no animation */}
              <div className="relative z-10" data-id="element-979">{item.icon}</div>

              {/* Label - no animation */}
              {isExpanded && <span className="text-sm font-medium relative z-10" data-id="element-980">
                  {item.label}
                </span>}
            </button>;
      })}
      </nav>

      {/* Footer */}
      <div className="border-t border-border-subtle p-3" data-id="element-981">
        <button className={`
            w-full flex items-center gap-3 px-3 py-2.5 text-text-secondary
            hover:text-text-primary hover:bg-background-elevated rounded transition-colors
            ${!isExpanded && 'justify-center'}
          `} title={!isExpanded ? 'Help' : undefined} data-id="element-982">
          <HelpCircle size={20} data-id="element-983" />
          {isExpanded && <span className="text-sm font-medium" data-id="element-984">Help</span>}
        </button>
      </div>
    </div>;
}